/**
 * Card for a session.
 */
import * as React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Calendar,
  Clock,
  Video,
  FileEdit,
  Eye,
  Users,
  GraduationCap,
  BookOpen,
  UserCheck,
  UsersRound,
} from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { VisioPopupBlockedModal } from '@/components/common/modals/VisioPopupBlockedModal';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  PAIR_ONE_OFF_BADGE_CONFIG,
  SESSION_REMOTE_STATUS_CONFIG,
  SESSION_REMOTE_TYPE_CONFIG,
  SESSION_RECURRENCE_CONFIG,
  JOINABLE_SESSION_STATUSES,
} from '@/components/sessions/session-configs';
import type { SessionTab, ListItem } from '@/types/remote/session.types';
import { useJoinRemoteMeeting } from '@/hooks/useJoinRemoteMeeting';
import { isPairOneOffSession } from '@/components/sessions/remote/session-form-utils';
import {
  formatTime,
  calcDurationMinutes,
  parseSessionDate,
  isSessionTerminated,
  getSessionReportState,
  getCanViewReport,
  deriveSessionParticipants,
  getEditTooltip,
  getCancelTooltip,
  getReportTooltip,
  useNavigateToRemoteReport,
  type SessionItemPageActions,
} from './session-table-utils';

/** Props for {@link SessionCard}. */
interface SessionCardProps {
  session: ListItem;
  activeTab: SessionTab;
  onEdit?: (sessionId: string) => void;
  onView?: (sessionId: string) => void;
  onCancel?: (sessionId: string) => void;
  pageActions?: SessionItemPageActions;
}

/** Minimal person shape for participant lines (name + overflow tooltip). */
type PersonCard = {
  id?: string | null;
  user?: { id?: string; firstName?: string | null; lastName?: string | null } | null;
};

/** Single student name, or a count with a tooltip listing everyone. */
function renderStudentsCard(students: PersonCard[]): React.ReactNode {
  const count = students.length;
  if (count === 0) return <span className="text-muted-foreground">—</span>;
  if (count === 1) {
    return (
      <span className="truncate">
        {students[0].user?.firstName} {students[0].user?.lastName}
      </span>
    );
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1 cursor-default">
            <Users className="w-3 h-3 text-[var(--color-1-nuance-1)] shrink-0" />
            <span>{count} élèves</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-0.5">
            {students.map((s) => (
              <p key={s.id ?? s.user?.id}>
                {s.user?.firstName} {s.user?.lastName}
              </p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Single animator name, or a count with a tooltip listing everyone. */
function renderAnimatorsCard(animators: PersonCard[]): React.ReactNode {
  const count = animators.length;
  if (count === 0) return <span>0</span>;
  if (count === 1) {
    return (
      <span className="truncate">
        {animators[0].user?.firstName} {animators[0].user?.lastName}
      </span>
    );
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1 cursor-default">
            <Users className="w-3 h-3 text-[var(--color-1-nuance-1)] shrink-0" />
            <span>{count} animateurs</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-0.5">
            {animators.map((a) => (
              <p key={a.id ?? a.user?.id}>
                {a.user?.firstName} {a.user?.lastName}
              </p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Welcome-meeting lines: participants, then students, then parents. */
function renderWelcomeCard(
  welcomeParticipants: PersonCard[],
  students: PersonCard[],
  parents: PersonCard[],
): React.ReactNode {
  const count = welcomeParticipants.length;
  if (count === 0) return <span className="text-muted-foreground">0</span>;
  if (count === 1) {
    return (
      <span className="truncate">
        {welcomeParticipants[0].user?.firstName} {welcomeParticipants[0].user?.lastName}
      </span>
    );
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1 cursor-default">
            <Users className="w-3 h-3 text-[var(--color-1-nuance-1)] shrink-0" />
            <span>{count} participants</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-0.5">
            {students.map((s) => (
              <p key={s.id ?? s.user?.id}>
                {s.user?.firstName} {s.user?.lastName}
                <span className="text-muted-foreground ml-1 text-[10px]">(élève)</span>
              </p>
            ))}
            {parents.map((p) => (
              <p key={p.id ?? p.user?.id}>
                {p.user?.firstName} {p.user?.lastName}
                <span className="text-muted-foreground ml-1 text-[10px]">(parent)</span>
              </p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Open the report editor for this session (creates a draft when `reportId` is missing). */
function editReport(
  navigateToReport: (id: string, type: string, reportId?: string | null) => Promise<void>,
  sessionId: string,
  sessionType: string,
  reportId?: string | null,
): void {
  void navigateToReport(sessionId, sessionType, reportId);
}

/** Tutoring card: tutor name plus student list. */
function renderTutoringParticipantsCard(
  tutor: PersonCard | undefined,
  students: PersonCard[],
): React.ReactNode {
  return (
    <div className="space-y-1.5 mb-2">
      <div className="flex items-center gap-2 text-xs">
        <GraduationCap className="w-4 h-4 shrink-0" style={{ color: 'var(--color-1-nuance-1)' }} />
        <span className="font-medium shrink-0 text-muted-foreground">Tuteur :</span>
        {tutor ? (
          <span className="truncate">
            {tutor.user?.firstName} {tutor.user?.lastName}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </div>
      <div className="flex items-center gap-2 text-xs">
        <BookOpen className="w-4 h-4 shrink-0" style={{ color: 'var(--color-1-nuance-1)' }} />
        <span className="font-medium shrink-0 text-muted-foreground">
          Élève{students.length > 1 ? 's' : ''} :
        </span>
        {renderStudentsCard(students)}
      </div>
    </div>
  );
}

/** Meeting card: animators / volunteers, or welcome-meeting groups. */
function renderMeetingParticipantsCard(
  activeTab: SessionTab,
  maxStudents: number | null | undefined,
  animators: PersonCard[],
  volunteers: PersonCard[],
  welcomeParticipants: PersonCard[],
  students: PersonCard[],
  parents: PersonCard[],
): React.ReactNode {
  return (
    <div className="space-y-1.5 mb-2">
      <div className="flex items-center gap-2 text-xs">
        <UserCheck className="w-4 h-4 shrink-0" style={{ color: 'var(--color-1-nuance-1)' }} />
        <span className="font-medium shrink-0 text-muted-foreground">
          Animateur{animators.length > 1 ? 's' : ''} :
        </span>
        {renderAnimatorsCard(animators)}
      </div>
      {activeTab === 'welcome-meeting' && (
        <div className="flex items-center gap-2 text-xs">
          <Users className="w-4 h-4 shrink-0" style={{ color: 'var(--color-1-nuance-1)' }} />
          <span className="font-medium shrink-0 text-muted-foreground">Participants :</span>
          {renderWelcomeCard(welcomeParticipants, students, parents)}
        </div>
      )}
      {activeTab === 'information-meeting' && (
        <div className="flex items-center gap-2 text-xs">
          <UsersRound className="w-4 h-4 shrink-0" style={{ color: 'var(--color-1-nuance-1)' }} />
          <span className="font-medium shrink-0 text-muted-foreground">Participants :</span>
          {volunteers.length === 0 ? (
            <span className="text-muted-foreground">0{maxStudents ? ` / ${maxStudents}` : ''}</span>
          ) : (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-default">
                    <Users className="w-3 h-3 text-[var(--color-1-nuance-1)] shrink-0" />
                    <span>
                      {volunteers.length}
                      {maxStudents ? ` / ${maxStudents}` : ''} participant
                      {volunteers.length > 1 ? 's' : ''}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-0.5">
                    {volunteers.map((v) => (
                      <p key={v.id ?? v.user?.id}>
                        {v.user?.firstName} {v.user?.lastName}
                      </p>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
    </div>
  );
}

/** Args for {@link renderCardParticipants}. */
interface CardParticipantsArgs {
  activeTab: SessionTab;
  session: ListItem;
  tutor: PersonCard | undefined;
  students: PersonCard[];
  parents: PersonCard[];
  animators: PersonCard[];
  volunteers: PersonCard[];
  welcomeParticipants: PersonCard[];
}

/** Participant chips on a remote session card, by tab. */
function renderCardParticipants({
  activeTab,
  session,
  tutor,
  students,
  parents,
  animators,
  volunteers,
  welcomeParticipants,
}: CardParticipantsArgs): React.ReactNode {
  if (activeTab === 'tutoring') {
    return renderTutoringParticipantsCard(tutor, students);
  }
  if (
    activeTab === 'information-meeting' ||
    activeTab === 'welcome-meeting' ||
    activeTab === 'field-event'
  ) {
    return renderMeetingParticipantsCard(
      activeTab,
      session.maxStudents,
      animators,
      volunteers,
      welcomeParticipants,
      students,
      parents,
    );
  }
  return null;
}

/** Props for {@link renderJoinMeetingButtonCard}. */
interface JoinButtonCardProps {
  session: ListItem;
  type: string;
  canJoin: boolean;
  join: (id: string) => Promise<void>;
  isJoining: boolean;
  tooltipText: string;
}

/** Join-visio icon on a remote session card when the type is joinable. */
function renderJoinMeetingButtonCard({
  session,
  type,
  canJoin,
  join,
  isJoining,
  tooltipText,
}: JoinButtonCardProps): React.ReactNode {
  if (session.type !== type) return null;
  if (!canJoin) return null;
  if (!JOINABLE_SESSION_STATUSES.has(session.status)) return null;
  return (
    <Button
      variant="outline"
      color="rgb(var(--color-error))"
      size="icon"
      loading={isJoining}
      className={session.status === 'IN_PROGRESS' ? 'session-video-blink' : ''}
      onClick={(e) => {
        e.stopPropagation();
        void join(session.id);
      }}
      icon={Video}
      tooltipText={tooltipText}
    />
  );
}

/** Props for {@link SessionCardActionsFooter}. */
interface SessionCardActionsFooterProps {
  session: ListItem;
  canEdit: boolean;
  canCancel: boolean;
  canJoinWelcomeMeeting: boolean;
  canJoinInformationMeeting: boolean;
  canViewReport: boolean;
  onEdit?: (id: string) => void;
  onView?: (id: string) => void;
  onCancel?: (id: string) => void;
}

/** Edit / cancel / join / report actions on a remote session card. */
function SessionCardActionsFooter({
  session,
  canEdit,
  canCancel,
  canJoinWelcomeMeeting,
  canJoinInformationMeeting,
  canViewReport,
  onEdit,
  onView,
  onCancel,
}: Readonly<SessionCardActionsFooterProps>): React.ReactElement {
  const { navigateToReport, isLoading: isLoadingReport } = useNavigateToRemoteReport();
  const { isJoining, join, popupBlocked } = useJoinRemoteMeeting();

  const isTerminated = isSessionTerminated(session.status);
  const { hasReport, isReportValidated, reportDisabled } = getSessionReportState(
    session,
    isLoadingReport,
  );

  const handleReportClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    editReport(navigateToReport, session.id, session.type, session.compteRenduId);
  };

  return (
    <div className="data-card-footer">
      <div className="flex gap-2">
        {renderJoinMeetingButtonCard({
          session,
          type: 'welcome_meeting',
          canJoin: canJoinWelcomeMeeting,
          join,
          isJoining,
          tooltipText: "Rejoindre réunion d'accueil",
        })}
        {renderJoinMeetingButtonCard({
          session,
          type: 'information_meeting',
          canJoin: canJoinInformationMeeting,
          join,
          isJoining,
          tooltipText: "Rejoindre réunion d'information",
        })}
        <VisioPopupBlockedModal {...popupBlocked} />

        {onView && (
          <Button
            preset="row-view"
            onClick={(e) => {
              e.stopPropagation();
              onView(session.id);
            }}
          />
        )}

        {canEdit && onEdit && (
          <Button
            preset="row-edit"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(session.id);
            }}
            disabled={isTerminated}
            tooltipText={getEditTooltip(isTerminated, session.status)}
          />
        )}

        {canViewReport && (
          <Button
            variant="ghost"
            color="var(--color-3-nuance-1)"
            size="icon"
            onClick={handleReportClick}
            icon={isReportValidated ? Eye : FileEdit}
            disabled={reportDisabled}
            loading={isLoadingReport}
            tooltipText={getReportTooltip(
              isLoadingReport,
              isReportValidated,
              session.compteRenduStatus,
              hasReport,
              session.status,
            )}
          />
        )}

        {canCancel && onCancel && (
          <Button
            preset="row-cancel"
            onClick={(e) => {
              e.stopPropagation();
              onCancel(session.id);
            }}
            disabled={isTerminated}
            tooltipText={getCancelTooltip(isTerminated, session.status)}
          />
        )}
      </div>
    </div>
  );
}

/** Card for a session. */
export const SessionCard: React.FC<SessionCardProps> = ({
  session,
  activeTab,
  onEdit,
  onView,
  onCancel,
  pageActions,
}) => {
  const canEdit = pageActions?.canEditRow ?? true;
  const canCancel = pageActions?.canCancelRow ?? true;
  const canJoinWelcomeMeeting = pageActions?.canJoinWelcomeMeeting ?? false;
  const canJoinInformationMeeting = pageActions?.canJoinInformationMeeting ?? false;
  const canViewReport = getCanViewReport(session.type, pageActions);

  const statusConfig =
    SESSION_REMOTE_STATUS_CONFIG[session.status] || SESSION_REMOTE_STATUS_CONFIG.PLANNED;
  const StatusIcon = statusConfig?.icon;

  const parsedDate =
    session.sessionDate instanceof Date
      ? session.sessionDate
      : parseSessionDate(String(session.sessionDate ?? ''));
  const { tutor, students, parents, animators, volunteers, welcomeParticipants } =
    deriveSessionParticipants(session);
  const typeConfig = SESSION_REMOTE_TYPE_CONFIG[session.type];
  const showTypeBadges = activeTab === 'tutoring' || activeTab === 'all';
  const typeBadges = (
    <div className="ml-auto inline-flex flex-col items-center gap-0.5">
      <LabelBadge
        label={typeConfig?.label ?? session.type}
        icon={typeConfig?.icon}
        variant={typeConfig?.badgeVariant ?? 'ghost'}
        color={typeConfig?.badgeColor ?? 'var(--color-2-nuance-1)'}
        size="sm"
      />
      {isPairOneOffSession(session) ? (
        <LabelBadge
          label={PAIR_ONE_OFF_BADGE_CONFIG.label}
          icon={PAIR_ONE_OFF_BADGE_CONFIG.icon}
          variant={PAIR_ONE_OFF_BADGE_CONFIG.badgeVariant}
          color={PAIR_ONE_OFF_BADGE_CONFIG.badgeColor}
          size="sm"
        />
      ) : null}
      {session.recurrenceType &&
      session.recurrenceType !== 'none' &&
      session.recurrenceType !== 'once' ? (
        <LabelBadge
          label={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.label ?? 'Récurrente'}
          icon={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.icon}
          variant={SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.badgeVariant ?? 'outlined'}
          color={
            SESSION_RECURRENCE_CONFIG[session.recurrenceType]?.badgeColor ??
            'var(--color-2-nuance-1)'
          }
          size="sm"
        />
      ) : null}
    </div>
  );

  return (
    <TableCard onClick={() => onView?.(session.id)}>
      <div className="data-card-header">
        <LabelBadge
          label={statusConfig.label}
          icon={statusConfig.isInProgress ? <span className="session-dot-pulse" /> : StatusIcon}
          variant={statusConfig.badgeVariant}
          color={statusConfig.badgeColor}
          size="sm"
        />
        {showTypeBadges ? typeBadges : null}
      </div>

      <div className="data-card-content" style={{ overflow: 'hidden' }}>
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-foreground text-sm">
              {format(parsedDate, 'd MMMM yyyy', { locale: fr })}
            </span>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3 text-[var(--color-1-nuance-1)]" />
              <span>
                {formatTime(session.startTime)} (
                {session.durationMinutes ??
                  calcDurationMinutes(session.startTime, session.endTime) ??
                  '?'}{' '}
                min)
              </span>
            </div>
          </div>
        </div>

        {renderCardParticipants({
          activeTab,
          session,
          tutor,
          students,
          parents,
          animators,
          volunteers,
          welcomeParticipants,
        })}
      </div>

      <SessionCardActionsFooter
        session={session}
        canEdit={canEdit}
        canCancel={canCancel}
        canJoinWelcomeMeeting={canJoinWelcomeMeeting}
        canJoinInformationMeeting={canJoinInformationMeeting}
        canViewReport={canViewReport}
        onEdit={onEdit}
        onView={onView}
        onCancel={onCancel}
      />
    </TableCard>
  );
};
